import {
  CategoryLookupDto,
  ItemMenuCategoryDto,
  ItemMenuItemDetailsDto,
  ItemMenuItemType,
  ItemMenuSuboptionType,
} from '@/core';
import { last } from 'lodash';
import {
  CategoryLookupState,
  CategoryState,
  ItemMenuItemDetailsState,
} from '@/redux';
import { ItemIcon } from '../Items';
import { Col, Row, Space } from 'antd';
import { ItemDetailsPanel } from '../Items.Details';
import { dataNodeUtils, EnumValue, TextBox, YesNo } from '@/components';
import { useTranslation } from 'react-i18next';
import { ITEM_DESCRIPTION_PREFIX } from './convertCategoryAndItemsToTreeData';

export interface CategoryLookup {
  id: string;
  name: string;
}

export interface ItemMenuTreeTableNode {
  id: string;
  key: string;
  category?: CategoryLookup;
  subCategory1?: CategoryLookup;
  subCategory2?: CategoryLookup;
  item?: React.ReactNode;
  itemDescription?: boolean;
  max?: number;
  min?: number;
  explicit?: boolean;
  title?: string;
  children?: ItemMenuTreeTableNode[];
  hidden?: boolean;
}

interface Options {
  mapItem?: (item: ItemMenuItemDetailsState) => ItemMenuTreeTableNode;
  mapCategory?: (category: CategoryLookupState) => ItemMenuTreeTableNode;
  mapItemDescription?: (
    item: ItemMenuItemDetailsState,
  ) => ItemMenuTreeTableNode;
}

const defaultMapItem = (
  item: ItemMenuItemDetailsState,
): ItemMenuTreeTableNode => {
  return {
    key: item.id,
    id: item.id,
    item: (
      <Space>
        <ItemIcon color={COLOR_BY_TYPE[item.type]} />
        {item.name}
      </Space>
    ),
  };
};

const defaultMapCategory = (
  category: CategoryLookupDto,
): ItemMenuTreeTableNode => ({
  key: category.id,
  id: category.id,
});

const defaultMapItemDescription = (
  item: ItemMenuItemDetailsState,
): ItemMenuTreeTableNode => {
  return {
    id: ITEM_DESCRIPTION_PREFIX + item.id,
    key: ITEM_DESCRIPTION_PREFIX + item.id,
    itemDescription: true,
    item: <ItemDetails item={item} />,
  };
};

export const MIN_CATEGORY_ITEM_COUNT = 0;
export const MAX_CATEGORY_ITEM_COUNT = 99;
export const MIN_SUBCATEGORY_ITEM_COUNT = 0;
export const MAX_SUBCATEGORY_ITEM_COUNT = 99;

export function convertItemMenuItemsToTreeTableData(
  items: ItemMenuItemDetailsState[],
  categories: ItemMenuCategoryDto[],
  allCategories?: CategoryState[],
  options?: Options,
) {
  const treeData: ItemMenuTreeTableNode[] = [];
  const categoriesById: Record<string, ItemMenuTreeTableNode> = {};
  const {
    mapCategory = defaultMapCategory,
    mapItem = defaultMapItem,
    mapItemDescription = defaultMapItemDescription,
  } = options ?? {};

  function ensureCategoryPathExists(
    categoryPath: CategoryLookupDto[],
  ): ItemMenuTreeTableNode {
    const pathKey = last(categoryPath)!.id;

    if (categoriesById[pathKey]) {
      return categoriesById[pathKey];
    }

    let currentPath: ItemMenuTreeTableNode[] = treeData;
    let parentNode: ItemMenuTreeTableNode | undefined;

    categoryPath.forEach((category, index) => {
      const partialPathKey = category.id;

      const categoryReal = categories.find(
        (x) => x.categoryId === partialPathKey,
      );
      const maxInCategory = categoryReal?.max;
      const minInCategory = categoryReal?.min;
      const explicit = categoryReal?.explicit;
      if (!categoriesById[partialPathKey]) {
        const newNode: ItemMenuTreeTableNode = {
          ...mapCategory(category),
          category: categoryPath[0],
          subCategory1: index > 0 ? categoryPath[1] : undefined,
          subCategory2: index > 1 ? categoryPath[2] : undefined,
          max:
            maxInCategory ??
            (category.isRoot
              ? MAX_CATEGORY_ITEM_COUNT
              : MAX_SUBCATEGORY_ITEM_COUNT),
          min:
            minInCategory ??
            (category.isRoot
              ? MIN_CATEGORY_ITEM_COUNT
              : MIN_SUBCATEGORY_ITEM_COUNT),
          explicit: explicit,
          children: [],
        };

        categoriesById[partialPathKey] = newNode;

        if (parentNode) {
          parentNode.children!.push(newNode);
        } else {
          currentPath.push(newNode);
        }

        parentNode = newNode;
      } else {
        parentNode = categoriesById[partialPathKey];
      }
    });

    return categoriesById[pathKey];
  }

  function visit(item: ItemMenuItemDetailsState) {
    const { categoryPath } = item;
    const categoryNode = ensureCategoryPathExists(categoryPath);

    if (categoryNode) {
      const newItemNode: ItemMenuTreeTableNode = {
        children: [{ ...mapItemDescription(item) }],
        category: categoryPath[0],
        subCategory1: categoryPath[1],
        subCategory2: categoryPath[2],
        title: item.name,
        ...mapItem(item),
      };
      categoryNode.children = categoryNode.children
        ? [newItemNode, ...categoryNode.children]
        : [newItemNode];
    }
  }

  function visitCategory(category: ItemMenuCategoryDto) {
    const tree = dataNodeUtils.convertFromMany(
      allCategories ?? [],
      'id',
      'name',
      'children',
    );
    const path = dataNodeUtils.findPathInAny(tree, category.categoryId);
    const categoryPath = path
      ? path.map((x, index) => ({
          id: x.key.toString(),
          name: x.title?.toString() ?? '',
          isRoot: index === 0,
        }))
      : null;
    if (categoryPath) ensureCategoryPathExists(categoryPath);
  }

  items.forEach(visit);
  categories.forEach(visitCategory);

  return treeData;
}

export const COLOR_BY_TYPE: Record<ItemMenuItemType, string> = {
  Standard: '#1fb197',
  Upgrade: '#efac4e',
};

export function ItemDetails({ item }: { item: ItemMenuItemDetailsDto }) {
  const { t } = useTranslation();
  return (
    <ItemDetailsPanel
      id={item.id}
      readonly
      includeUnlistedSuboptions={item.suboptions.map((x) => x.id)}
      selectableSuboption={{
        type: 'check',
        disabled: true,
        selected: item.suboptions.map((x) => x.id),
        onSelect: null!,
      }}
      detailsFooter={
        <div>
          <TextBox
            className="mb-2"
            label={t('itemMenus.details.standardCredit')}
          >
            <YesNo>{item?.standardCredit}</YesNo>
          </TextBox>
          <EnumValue type={ItemMenuItemType} value={item?.type} />
        </div>
      }
      renderSuboptionItem={({ item: { id }, content }) => (
        <Row wrap={false} align="middle">
          <Col flex="auto">{content}</Col>
          <Col flex="none">
            <EnumValue
              type={ItemMenuSuboptionType}
              value={item.suboptions?.find((x) => x.id === id)?.type}
            />
          </Col>
        </Row>
      )}
    />
  );
}
